import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { apiErrorMessage } from "../../lib/api";
import DashboardLayout from "../../components/layout/DashboardLayout";
import StatusBadge from "../../components/StatusBadge";
import { formatCLP, formatUSD, formatDateTime } from "../../lib/format";

export default function AdminDashboard() {
    const [data, setData] = useState(null);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        setLoading(true);
        setError("");
        api.get("/admin/dashboard")
            .then(({ data }) => setData(data))
            .catch((err) => {
                const msg = apiErrorMessage(err);
                setError(msg);
                console.error("[admin/dashboard] failed:", err?.response?.status, err?.response?.data, err);
            })
            .finally(() => setLoading(false));
    }, []);

    return (
        <DashboardLayout role="admin">
            <div className="mb-10" data-testid="admin-dashboard">
                <div className="label-mono mb-3">/ Admin · Dashboard</div>
                <h1 className="font-heading font-black text-3xl lg:text-4xl tracking-tighter">Control center</h1>
                <p className="text-avx-muted mt-2">Overview of the AVX Pro platform.</p>
            </div>

            {error && (
                <div className="panel p-4 mb-6 border-avx-danger/40 bg-avx-danger/8" data-testid="admin-dashboard-error">
                    <div className="label-mono text-avx-danger mb-1">/ Error loading the dashboard</div>
                    <div className="text-sm text-avx-text/90 font-mono break-words">{error}</div>
                    <div className="text-[10px] font-mono text-avx-muted mt-2">
                        KPIs and lists could not be loaded. Check the backend logs or open the browser console for more details.
                    </div>
                </div>
            )}

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
                <Kpi label="Users" value={data?.total_users ?? "—"} testId="kpi-users" />
                <Kpi label="Products" value={data?.total_products ?? "—"} testId="kpi-products" />
                <Kpi label="Paid purchases" value={data?.total_paid ?? "—"} accent="success" testId="kpi-paid" />
                <Kpi label="Pending purchases" value={data?.total_pending ?? "—"} accent="warn" testId="kpi-pending" />
                <Kpi label="Downloads" value={data?.total_downloads ?? "—"} testId="kpi-downloads" />
                <Kpi label="Revenue CLP" value={formatCLP(data?.revenue_clp ?? 0)} accent="primary" testId="kpi-revenue-clp" />
                <Kpi label="Revenue USD" value={formatUSD(data?.revenue_usd ?? 0)} accent="primary" testId="kpi-revenue-usd" />
                <div className="panel-elevated p-5 flex flex-col justify-center">
                    <Link to="/admin/assign" className="text-sm text-avx-blue hover:underline font-mono uppercase tracking-[0.18em]">+ Assign purchase</Link>
                </div>
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
                <Section title="Latest users" linkTo="/admin/users">
                    {(data?.latest_users || []).map((u) => (
                        <div key={u.id} className="flex items-center justify-between py-2 border-b border-avx-line/60 last:border-0">
                            <div>
                                <div className="text-sm">{u.name}</div>
                                <div className="text-xs font-mono text-avx-muted">{u.email}</div>
                            </div>
                            <span className="label-mono">{u.role}</span>
                        </div>
                    ))}
                </Section>
                <Section title="Latest purchases" linkTo="/admin/purchases">
                    {(data?.latest_purchases || []).map((p) => (
                        <div key={p.id} className="flex items-center justify-between py-2 border-b border-avx-line/60 last:border-0">
                            <div>
                                <div className="text-sm">{p.product?.name || "—"}</div>
                                <div className="text-xs font-mono text-avx-muted">{p.user?.email}</div>
                            </div>
                            <div className="text-right">
                                <StatusBadge status={p.status} />
                                <div className="text-[10px] font-mono text-avx-muted mt-1">{formatDateTime(p.created_at)}</div>
                            </div>
                        </div>
                    ))}
                </Section>
                <Section title="Latest downloads" linkTo="/admin/downloads">
                    {(data?.latest_downloads || []).map((d) => (
                        <div key={d.id} className="flex items-center justify-between py-2 border-b border-avx-line/60 last:border-0">
                            <div>
                                <div className="text-sm">{d.product_name}</div>
                                <div className="text-xs font-mono text-avx-muted">{d.user?.email}</div>
                            </div>
                            <div className="text-right text-[10px] font-mono text-avx-muted">{d.os} · {formatDateTime(d.downloaded_at)}</div>
                        </div>
                    ))}
                </Section>
                <Section title="Latest tickets" linkTo="/admin/support">
                    {(data?.latest_tickets || []).map((t) => (
                        <div key={t.id} className="flex items-center justify-between py-2 border-b border-avx-line/60 last:border-0">
                            <div>
                                <div className="text-sm">{t.subject}</div>
                                <div className="text-xs font-mono text-avx-muted">{t.user_email}</div>
                            </div>
                            <StatusBadge status={t.status} />
                        </div>
                    ))}
                </Section>
            </div>
        </DashboardLayout>
    );
}

function Kpi({ label, value, accent, testId }) {
    const accentCls =
        accent === "success" ? "text-avx-success" :
        accent === "warn" ? "text-avx-blue" :
        accent === "primary" ? "text-avx-blue" : "text-avx-text";
    return (
        <div className="panel-elevated p-5" data-testid={testId}>
            <div className="label-mono mb-2">{label}</div>
            <div className={`font-heading font-black text-2xl ${accentCls}`}>{value}</div>
        </div>
    );
}

function Section({ title, linkTo, children }) {
    return (
        <div className="panel p-5">
            <div className="flex justify-between items-center mb-3">
                <div className="label-mono">/ {title}</div>
                {linkTo && <Link to={linkTo} className="text-xs font-mono uppercase tracking-[0.18em] text-avx-blue hover:underline">View →</Link>}
            </div>
            {children}
        </div>
    );
}
